import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, Square, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const MAX_SECONDS = 120;
const pickType = () => ["audio/mp4", "audio/webm;codecs=opus", "audio/webm"].find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || "";

export const VoiceButton = ({ hint = "", onText, id = "voice" }) => {
  const [state, setState] = useState("idle");
  const [secs, setSecs] = useState(0);
  const rec = useRef(null);
  const timer = useRef(null);
  useEffect(() => () => { clearInterval(timer.current); rec.current?.stream?.getTracks().forEach((t) => t.stop()); }, []);

  const upload = async (blob, type) => {
    setState("busy");
    const fd = new FormData();
    fd.append("audio", blob, `clip.${type.includes("mp4") ? "mp4" : "webm"}`);
    fd.append("hint", hint);
    try { const { data } = await api.post("/transcribe", fd); if (data?.text) onText(data.text); else toast.error("Didn't catch anything. Try again a bit louder"); }
    catch (e) { toast.error(formatApiError(e)); }
    setState("idle");
  };

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) { toast.error("Voice isn't supported in this browser"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const type = pickType();
      const r = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
      const chunks = [];
      r.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      r.onstop = () => { stream.getTracks().forEach((t) => t.stop()); clearInterval(timer.current); const t = r.mimeType || type || "audio/webm"; upload(new Blob(chunks, { type: t }), t); };
      rec.current = r;
      r.start();
      setSecs(0);
      setState("rec");
      timer.current = setInterval(() => setSecs((s) => { if (s + 1 >= MAX_SECONDS) r.state === "recording" && r.stop(); return s + 1; }), 1000);
    } catch { toast.error("Allow microphone access to speak your answer"); }
  };
  const stop = () => { if (rec.current?.state === "recording") rec.current.stop(); };

  if (state === "busy") return <span data-testid={`${id}-busy`} className="inline-flex items-center gap-1.5 h-9 rounded-full border border-gold-400/50 px-3 text-xs font-semibold text-gold-300"><Loader2 className="w-3.5 h-3.5 animate-spin" />Writing it up…</span>;
  if (state === "rec") return (
    <button type="button" data-testid={`${id}-stop`} onClick={stop} className="inline-flex items-center gap-1.5 h-9 rounded-full bg-brand px-3.5 text-xs font-bold text-white shadow-red">
      <span className="w-2 h-2 rounded-full bg-white animate-pulse" /><Square className="w-3 h-3" />Stop · {Math.floor(secs / 60)}:{String(secs % 60).padStart(2, "0")}
    </button>
  );
  return <button type="button" data-testid={`${id}-start`} onClick={start} aria-label="Speak your answer" className="inline-flex items-center gap-1.5 h-9 rounded-full border border-slate-200 px-3.5 text-xs font-semibold text-slate-700 hover:border-gold-400/60 hover:text-white"><Mic className="w-3.5 h-3.5" />Speak</button>;
};
